/**
 * Maps a react-query result onto the props the DS table reads, and splits `isFetching` into the two
 * states it treats differently.
 *
 * `loading` is the FIRST load: the DS returns `initialLoading` and replaces the body with a
 * skeleton. A refetch must NOT claim it — the rows are still on screen and would be unmounted
 * mid-read. That belongs to `refetching`, which holds them under an overlay.
 *
 * `keepPreviousData` is NOT the trigger — it only matters when the query KEY changes. A same-key
 * background refetch keeps the cached rows mounted just the same, so ANY in-flight fetch over rows
 * already on screen belongs in `refetching`. Every table here needs this rather than passing
 * `isFetching` straight through; assuming otherwise is what left one table unfixed twice.
 *
 * The error trio is passed through so a rejected fetch renders the DS error overlay with a retry,
 * rather than the empty state — a failed request is not "no data".
 */
export const tableQueryState = ({
  error,
  hasData,
  isError,
  isFetching,
  isInitialized,
  refetch,
}: {
  hasData: boolean
  isFetching: boolean
  isInitialized: boolean
  /** The query rejected. Omit for a table with no error affordance. */
  isError?: boolean
  /** Kept inspectable rather than swallowed. */
  error?: unknown
  /**
   * Re-runs the query behind the error overlay's retry button. Typed loosely because react-query's
   * `refetch` resolves a result object, while the DS slot is a plain `() => void`.
   */
  refetch?: () => unknown
}) => ({
  loading: !isInitialized || (isFetching && !hasData),
  refetching: isFetching && hasData,
  isError,
  error,
  // The promise is discarded EXPLICITLY: retry outcomes come back through the query state this
  // helper already maps, so awaiting here would have nothing to report.
  onRetry:
    refetch === undefined
      ? undefined
      : () => {
          void refetch()
        },
})
